import { useWriteContract, useSimulateContract } from 'wagmi';

import ExplainUnlisting from 'components/ExplainUnlisting';
import TransactionModal from 'components/TransactionModal';
import Modal from 'components/base/Modal';
import { LIST_CTA_MAP, LIST_HEADING_MAP } from 'copy/sell';
import { sellTrackingCopy } from 'copy/transactions';
import useTransactionStore from 'state/stores/transactions';
import { getUnlistPrepareContractWriteConfig } from 'web3/sell';

import { useNft } from 'gql/api/queries/nft.generated';
import { useQueryEffects } from 'hooks/react-query';
import useModalVisibility from 'hooks/use-modal-visibility';
import { useExhibitionIdForNft } from 'hooks/web3/use-exhibition-id-for-nft';
import { useNftSeller } from 'hooks/web3/use-nft-seller';
import { ZERO_ADDRESS } from 'lib/constants';
import { logger } from 'lib/logger';
import { getTokenFilter } from 'utils/inputs';
import { mapApiMediaToPreviewMediaAsset } from 'utils/media-preview';
import { extractPrepareContractWriteRevertReason } from 'utils/revert-reasons';

import { ModalOptions } from 'types/modal';

const MODAL_KEY = 'REMOVE_LISTINGS';
const ACTION = 'unlist-nft';

type RemoveListingsOptions = ModalOptions<typeof MODAL_KEY>;

export default function RemoveListings() {
  const modal = useModalVisibility(MODAL_KEY);

  return (
    <Modal.Root open={modal.open} onOpenChange={modal.onOpenChange}>
      <Modal.Portal>
        <Modal.BlurOverlay />
        <Modal.PositionOverlay>
          <Modal.UnmountListener onUnmount={modal.onUnmount} />
          {modal.config && <RemoveListingsWindow {...modal.config} />}
        </Modal.PositionOverlay>
      </Modal.Portal>
    </Modal.Root>
  );
}

function RemoveListingsWindow(props: RemoveListingsOptions) {
  const txStore = useTransactionStore();
  const nftFilter = getTokenFilter(props.nft);

  const nftQuery = useNft(nftFilter, {
    select: ({ nft }) => {
      if (!nft)
        return {
          nft: null,
          media: null,
        };

      return {
        nft,
        media: nft.media ? mapApiMediaToPreviewMediaAsset(nft.media) : null,
      };
    },
  });
  const nft = nftQuery.isSuccess ? nftQuery.data.nft : null;
  const media = nftQuery.isSuccess ? nftQuery.data.media : null;

  const simulateUnlistNft = useSimulateContract(
    getUnlistPrepareContractWriteConfig(nftFilter)
  );

  useQueryEffects(simulateUnlistNft, {
    onError(error) {
      logger.error(
        `unlist-nft simulation error ${nftFilter.contractAddress} ${nftFilter.tokenId}`,
        { ...error }
      );
    },
  });

  const nftSellerQuery = useNftSeller(nftFilter);

  const chainId = nftFilter.chainId;

  const exhibitionIdForNft = useExhibitionIdForNft(
    {
      nftFilter,
      sellerPublicKey: nftSellerQuery.data || ZERO_ADDRESS,
    },
    { enabled: Boolean(nftSellerQuery.data) }
  );

  const contractWrite = useWriteContract({
    mutation: {
      onSuccess: (txHash) => {
        txStore.startTracking({
          ...sellTrackingCopy.unlist(),
          chainId,
          action: {
            name: ACTION,
            worldId: exhibitionIdForNft.data
              ? Number(exhibitionIdForNft.data.worldId)
              : null,
          },
          ui: 'toast',
          txHash,
        });
      },
    },
  });

  const contractError =
    extractPrepareContractWriteRevertReason(simulateUnlistNft);

  return (
    <TransactionModal
      chainId={chainId}
      preSignPrompt={{
        type: 'skip',
      }}
    >
      <TransactionModal.Content
        action={ACTION}
        chainId={chainId}
        txHash={contractWrite.data || null}
        header={<Modal.Header title={LIST_HEADING_MAP.UNLIST} />}
        footer={
          <Modal.Footer>
            <TransactionModal.TransactionButtonWithContractApproval
              chainId={chainId}
              label={LIST_CTA_MAP.UNLIST}
              write={() => {
                if (simulateUnlistNft.isSuccess) {
                  contractWrite.writeContract(simulateUnlistNft.data.request);
                }
              }}
              isDisabled={!simulateUnlistNft.isSuccess}
              isLoading={contractWrite.isPending || Boolean(contractWrite.data)}
              onApprovalSuccess={simulateUnlistNft.refetch}
              contractAddress={nftFilter.contractAddress}
              error={contractError}
            />
          </Modal.Footer>
        }
      >
        <TransactionModal.Body>
          <ExplainUnlisting nft={nft} media={media} />
        </TransactionModal.Body>
      </TransactionModal.Content>
    </TransactionModal>
  );
}
